// src/Leads.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './lib/supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { recomputeScores } from './lib/scoringEngine';

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  // New lead form
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [leadSource, setLeadSource] = useState('Manual');
  const [discoveredBy, setDiscoveredBy] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [message, setMessage] = useState(''); // ERRORS ONLY (no success banner)

  // UI state
  const [openCommentId, setOpenCommentId] = useState(null);
  const [showArchived, setShowArchived] = useState(() => {
    const s = localStorage.getItem('showArchived');
    return s ? JSON.parse(s) : false;
  });

  // Filters
  const [searchInput, setSearchInput] = useState(() => localStorage.getItem('leadSearch') || '');
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('leadSearch') || '');
  const [filterStatus, setFilterStatus] = useState(() => localStorage.getItem('filterStatus') || 'All');
  const [filterSource, setFilterSource] = useState(() => localStorage.getItem('filterSource') || 'All');

  // Pagination
  const [pageSize, setPageSize] = useState(() => {
    const raw = parseInt(localStorage.getItem('leadPageSize') || '25', 10);
    return [10, 25, 50, 100].includes(raw) ? raw : 25;
  });
  const [page, setPage] = useState(1);

  // Selection for batch actions
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Inline comment edits (keyed by lead.id or "new")
  const [commentEdits, setCommentEdits] = useState({});

  // Sort (persisted)
  const [sortConfig, setSortConfig] = useState(() => {
    const stored = localStorage.getItem('leadSort');
    return stored ? JSON.parse(stored) : { key: 'date_added', direction: 'desc' };
  });

  const navigate = useNavigate();
  const searchRef = useRef(null);

  // -------- Inline mini chips (per row/per control) --------
  // chips: { [id]: { note?:Chip, actions?:Chip } }
  const [chips, setChips] = useState({});
  const CHIP_TTL = 1500;

  const pushChip = (id, slot, label, kind) => {
    const until = Date.now() + CHIP_TTL;
    setChips((prev) => {
      const row = prev[id] || {};
      return { ...prev, [id]: { ...row, [slot]: { label, kind, until } } };
    });
    setTimeout(() => {
      setChips((prev) => {
        const row = prev[id];
        if (!row) return prev;
        const entry = row[slot];
        if (!entry || entry.until > Date.now()) return prev;
        const { [slot]: _drop, ...restSlots } = row;
        if (Object.keys(restSlots).length === 0) {
          const { [id]: _rowDrop, ...restRows } = prev;
          return restRows;
        }
        return { ...prev, [id]: restSlots };
      });
    }, CHIP_TTL + 50);
  };

  const MiniChip = ({ label, kind }) => {
    const base = 'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium';
    const styles = {
      saved: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
      stamped: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200',
      updated: 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200',
      error: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
    };
    return <span className={`${base} ${styles[kind] || styles.updated}`}>{label}</span>;
  };

  // ---------- Persist simple prefs ----------
  useEffect(() => { localStorage.setItem('showArchived', JSON.stringify(showArchived)); }, [showArchived]);
  useEffect(() => { localStorage.setItem('leadSearch', searchTerm); }, [searchTerm]);
  useEffect(() => { localStorage.setItem('filterStatus', filterStatus); }, [filterStatus]);
  useEffect(() => { localStorage.setItem('filterSource', filterSource); }, [filterSource]);
  useEffect(() => { localStorage.setItem('leadPageSize', String(pageSize)); }, [pageSize]);

  // Reset to page 1 when filters or pageSize change
  useEffect(() => { setPage(1); }, [searchTerm, filterStatus, filterSource, showArchived, pageSize]);

  // Debounce search input -> searchTerm
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 220);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ---------- Data fetch ----------
  const fetchLeads = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('leads').select('*');
    if (error) {
      console.error('Fetch error:', error.message);
      setMessage('Error loading leads.'); // errors only
      setLoading(false);
      return;
    }
    let sortedData = [...data];
    if (sortConfig) {
      sortedData.sort((a, b) => {
        const { key, direction } = sortConfig;
        const dir = direction === 'asc' ? 1 : -1;
        const av = a?.[key];
        const bv = b?.[key];
        const aNorm = av == null ? '' : typeof av === 'string' ? av.toLowerCase() : av;
        const bNorm = bv == null ? '' : typeof bv === 'string' ? bv.toLowerCase() : bv;
        if (aNorm < bNorm) return -1 * dir;
        if (aNorm > bNorm) return 1 * dir;
        return 0;
      });
    }
    setLeads(sortedData);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLeads(); }, [sortConfig]);

  // ---------- Local row merge (prevents re-fetch jump) ----------
  const mergeRow = (id, patch) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const refreshSubset = async (ids) => {
    if (!ids || ids.length === 0) return;
    const { data, error } = await supabase
      .from('leads')
      .select('id,score,velocity,score_updated_at,velocity_updated_at,last_signal,updated_at,last_contacted')
      .in('id', ids);
    if (error) return;
    const map = new Map(data.map((r) => [r.id, r]));
    setLeads((prev) => prev.map((l) => (map.has(l.id) ? { ...l, ...map.get(l.id) } : l)));
  };

  // ---------- Create lead ----------
  // Manually adding a brand-new lead is legitimate (you're honestly recording
  // that you added this person). What's NOT legitimate is editing status,
  // score, source, or discovered-by on an EXISTING lead after the fact --
  // that fakes evidence the automated system is supposed to be producing.
  // New leads always start life as 'New' -- no picking a starting status.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    const normalizedEmail = (email || '').trim().toLowerCase();
    if (!normalizedEmail.includes('@') || !normalizedEmail.includes('.')) {
      setMessage('Invalid email format.');
      return;
    }

    const { data: existing, error: existErr } = await supabase
      .from('leads')
      .select('id')
      .eq('email', normalizedEmail);

    if (existErr) console.error('Check existing error:', existErr.message);
    if (existing?.length > 0) {
      setMessage('Email already exists.');
      return;
    }

    const payload = {
      name: name?.trim() || '',
      email: normalizedEmail,
      status: 'New',
      comment: commentEdits['new'] ?? '',
      lead_source: leadSource || null,
      discovered_by: discoveredBy || null,
      job_title: jobTitle?.trim() || null,
      company: company?.trim() || null,
      linkedin_url: linkedinUrl?.trim() || null,
      archived: false,
      last_contacted: null,
      score: 0,
      velocity: 'long',
      score_updated_at: new Date().toISOString(),
      velocity_updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase.from('leads').insert([payload]).select('*').single();
    if (error) {
      console.error('Insert error:', error.message);
      setMessage('Error saving lead.');
    } else {
      setName('');
      setEmail('');
      setLeadSource('Manual');
      setDiscoveredBy('');
      setJobTitle('');
      setCompany('');
      setLinkedinUrl('');
      setCommentEdits((p) => ({ ...p, new: '' }));
      setAddLeadOpen(false);
      // Put new lead at top of current dataset without hard refetch
      setLeads((prev) => [data, ...prev]);
      // Recompute (soft refresh)
      await recomputeScores([data.id]);
      await refreshSubset([data.id]);
      pushChip(data.id, 'actions', 'Added ✓', 'updated');
    }
  };

  // ---------- Updates (INLINE + NO JUMP) ----------
  const handleArchiveToggle = async (id, archived) => {
    setMessage('');
    mergeRow(id, { archived });
    const { error } = await supabase.from('leads').update({ archived }).eq('id', id);
    if (error) {
      console.error('Archive toggle error:', error.message);
      setMessage('Error updating archive.');
    } else {
      pushChip(id, 'actions', 'Updated ✓', 'updated');
    }
  };

  const handleCommentChange = (id, text) => {
    setCommentEdits((prev) => ({ ...prev, [id]: text }));
  };

  const handleSaveComment = async (e, id) => {
    e.preventDefault();
    setMessage('');
    const next = commentEdits[id] ?? '';
    mergeRow(id, { comment: next });
    const { error } = await supabase.from('leads').update({ comment: next }).eq('id', id);
    if (error) {
      console.error('Save comment error:', error.message);
      setMessage('Error saving comment.');
    } else {
      // show chip in both the Notes cell and the editor row
      pushChip(id, 'note', 'Saved ✓', 'saved');
      await recomputeScores([id]);
      await refreshSubset([id]);
    }
  };

  const handleBlurComment = async (id) => {
    if (commentEdits[id] === undefined) return;
    setMessage('');
    const next = commentEdits[id] ?? '';
    mergeRow(id, { comment: next });
    const { error } = await supabase.from('leads').update({ comment: next }).eq('id', id);
    if (error) {
      console.error('Autosave error:', error.message);
      setMessage('Error saving comment.');
    } else {
      pushChip(id, 'note', 'Saved ✓', 'saved');
      await recomputeScores([id]);
      await refreshSubset([id]);
    }
  };

  // ---------- Delete / logout ----------
  const handleDelete = async (id) => {
    setMessage('');
    const { error } = await supabase.from('leads').delete().eq('id', id);
    if (error) {
      console.error('Delete error:', error.message);
      setMessage('Error deleting lead.');
    } else {
      setLeads((prev) => prev.filter((l) => l.id !== id));
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // ---------- Sort ----------
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    const next = { key, direction };
    localStorage.setItem('leadSort', JSON.stringify(next));
    setSortConfig(next);
  };

  const sortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return <span className="ml-1 select-none">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
  };

  // ---------- Helpers ----------
  const toggleCommentEditor = (id) => {
    setOpenCommentId((cur) => (cur === id ? null : id));
    const lead = leads.find((l) => l.id === id);
    if (lead && commentEdits[id] === undefined) {
      setCommentEdits((p) => ({ ...p, [id]: lead.comment ?? '' }));
    }
  };

  const getRowClass = (s) => {
    switch (s) {
      case 'Interested': return 'bg-green-100';
      case 'Not Interested': return 'bg-red-100';
      case 'Closed': return 'bg-gray-200';
      default: return '';
    }
  };

  const formatDateTime = (v) => (v ? new Date(v).toLocaleString() : '');

  // ---------- Filter pipeline ----------
  const filteredLeads = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    return leads.filter((lead) => {
      if (!showArchived && lead.archived) return false;
      if (filterStatus !== 'All' && (lead.status || '') !== filterStatus) return false;
      if (filterSource !== 'All' && (lead.lead_source || '') !== filterSource) return false;
      if (!q) return true;
      const buckets = [
        (lead.name || ''),
        (lead.email || ''),
        (lead.status || ''),
        (lead.lead_source || ''),
        (lead.discovered_by || ''),
        (lead.comment || ''),
        (lead.velocity || ''),
        String(lead.score ?? ''),
      ].map(v => v.toLowerCase());
      return buckets.some(v => v.includes(q));
    });
  }, [leads, searchTerm, showArchived, filterStatus, filterSource]);

  // ---------- Pagination derived values ----------
  const totalFiltered = filteredLeads.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const sliceStart = (safePage - 1) * pageSize;
  const sliceEnd = Math.min(sliceStart + pageSize, totalFiltered);
  const pagedLeads = useMemo(
    () => filteredLeads.slice(sliceStart, sliceEnd),
    [filteredLeads, sliceStart, sliceEnd]
  );
  const displayStart = totalFiltered === 0 ? 0 : sliceStart + 1;
  const displayEnd = sliceEnd;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  // counts for ALL leads (respect archived toggle)
  const allStatusCounts = useMemo(() => {
    const counts = { New: 0, Interested: 0, 'Not Interested': 0, Closed: 0 };
    leads.forEach((lead) => {
      if (!showArchived && lead.archived) return;
      if (counts[lead.status] !== undefined) counts[lead.status]++;
    });
    return counts;
  }, [leads, showArchived]);

  // counts for FILTERED leads
  const statusCounts = useMemo(() => {
    const counts = { New: 0, Interested: 0, 'Not Interested': 0, Closed: 0 };
    filteredLeads.forEach((lead) => {
      if (counts[lead.status] !== undefined) counts[lead.status]++;
    });
    return counts;
  }, [filteredLeads]);

  // ---------- Selection helpers (PAGE-scoped) ----------
  const isAllPageSelected =
    selectedIds.size > 0 &&
    pagedLeads.length > 0 &&
    pagedLeads.every((l) => selectedIds.has(l.id));

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllPageSelected) {
        pagedLeads.forEach((l) => next.delete(l.id));
      } else {
        pagedLeads.forEach((l) => next.add(l.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // ---------- Batch actions ----------
  // Note: no batch status-setting here on purpose -- status is derived from
  // real interactions now, not something to bulk-flip by hand.
  const batchArchive = async (archived) => {
    if (selectedIds.size === 0) return;
    setMessage('');
    const ids = Array.from(selectedIds);
    // Optimistic
    setLeads((prev) => prev.map((l) => (ids.includes(l.id) ? { ...l, archived } : l)));
    const { error } = await supabase.from('leads').update({ archived }).in('id', ids);
    if (error) setMessage('Error updating archive (batch).');
    ids.forEach((id) => pushChip(id, 'actions', 'Updated ✓', 'updated'));
    clearSelection();
  };

  // Recompute scoring/velocity
  const recomputeSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setMessage('');
    const { error } = await recomputeScores(ids);
    if (error) setMessage('Error recomputing.');
    await refreshSubset(ids);
    ids.forEach((id) => pushChip(id, 'actions', 'Updated ✓', 'updated'));
    clearSelection();
  };

  const recomputeFiltered = async () => {
    const ids = filteredLeads.map(l => l.id);
    if (ids.length === 0) return;
    setMessage('');
    const { error } = await recomputeScores(ids);
    if (error) setMessage('Error recomputing.');
    await refreshSubset(ids);
    ids.forEach((id) => pushChip(id, 'actions', 'Updated ✓', 'updated'));
  };

  // ---------- Keyboard shortcuts ----------
  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      const editable = el.isContentEditable;
      return editable || tag === 'input' || tag === 'textarea' || tag === 'select';
    };

    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditable(document.activeElement)) return;

      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); return; }
      if (e.key === 'a') { e.preventDefault(); setShowArchived((prev) => !prev); return; }
      if (e.key === ',') { e.preventDefault(); setPage((p) => Math.max(1, p - 1)); return; }
      if (e.key === '.') { e.preventDefault(); setPage((p) => Math.min(totalPages, p + 1)); return; }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [totalPages]);

  // ---------- Pager UI ----------
  const Pager = () => {
    const canPrev = safePage > 1;
    const canNext = safePage < totalPages;
    return (
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{displayStart}</span>–<span className="font-medium">{displayEnd}</span> of <span className="font-medium">{totalFiltered}</span> filtered leads
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Shortcuts: <kbd className="rounded border px-1">/</kbd> focus search • <kbd className="rounded border px-1">a</kbd> toggle archived • <kbd className="rounded border px-1">,</kbd> prev • <kbd className="rounded border px-1">.</kbd> next
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <span>Rows per page</span>
            <select
              className="rounded border p-1"
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <div className="flex items-center gap-1">
            <button type="button" className={`rounded border px-2 py-1 text-sm ${canPrev ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`} onClick={() => canPrev && setPage(1)} disabled={!canPrev} title="First page">« First</button>
            <button type="button" className={`rounded border px-2 py-1 text-sm ${canPrev ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`} onClick={() => canPrev && setPage((p) => Math.max(1, p - 1))} disabled={!canPrev} title="Previous page">‹ Prev</button>
            <span className="px-2 text-sm text-gray-600">Page <span className="font-medium">{safePage}</span> / <span className="font-medium">{totalPages}</span></span>
            <button type="button" className={`rounded border px-2 py-1 text-sm ${canNext ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`} onClick={() => canNext && setPage((p) => Math.min(totalPages, p + 1))} disabled={!canNext} title="Next page">Next ›</button>
            <button type="button" className={`rounded border px-2 py-1 text-sm ${canNext ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`} onClick={() => canNext && setPage(totalPages)} disabled={!canNext} title="Last page">Last »</button>
          </div>
        </div>
      </div>
    );
  };

  // ---------- Render ----------
  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto max-w-7xl rounded bg-white p-6 shadow">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-bold">Leads Dashboard</h1>

          {/* Show Archived toggle */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </div>

        {/* Filters row */}
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search (name, email, status, source, discovered-by, notes, score, velocity)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full max-w-xl rounded border p-2"
            autoComplete="off"
          />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded border p-2" title="Filter by status">
            <option>All</option><option>New</option><option>Interested</option><option>Not Interested</option><option>Closed</option>
          </select>
          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="rounded border p-2" title="Filter by lead source">
            <option>All</option><option>Manual</option><option>Discord</option><option>X</option><option>Reddit</option><option>Website</option><option>Referral</option><option>Other</option>
          </select>
          <button type="button" className="rounded bg-gray-100 px-3 py-2 text-sm hover:bg-gray-200" onClick={() => { setSearchInput(''); setFilterStatus('All'); setFilterSource('All'); }}>
            Reset filters
          </button>
        </div>

        {/* Status & Actions Bar */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-3">
            <span className="rounded bg-gray-300 px-3 py-1 text-sm">New: {allStatusCounts['New']} ({statusCounts['New']})</span>
            <span className="rounded bg-green-200 px-3 py-1 text-sm">Interested: {allStatusCounts['Interested']} ({statusCounts['Interested']})</span>
            <span className="rounded bg-red-200 px-3 py-1 text-sm">Not Interested: {allStatusCounts['Not Interested']} ({statusCounts['Not Interested']})</span>
            <span className="rounded bg-gray-400 px-3 py-1 text-sm text-white">Closed: {allStatusCounts['Closed']} ({statusCounts['Closed']})</span>
          </div>

          {/* Batch action bar */}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={toggleSelectAllPage} className="rounded bg-gray-100 px-2 py-1 text-xs hover:bg-gray-200">
              {isAllPageSelected ? 'Unselect All (Page)' : 'Select All (Page)'}
            </button>

            <div className="hidden md:block h-4 w-px bg-gray-300" />

            <button type="button" onClick={() => batchArchive(true)} disabled={selectedIds.size === 0} className={`rounded px-2 py-1 text-xs ${selectedIds.size ? 'bg-yellow-100 hover:bg-yellow-200' : 'bg-yellow-100 opacity-50'}`}>Archive</button>
            <button type="button" onClick={() => batchArchive(false)} disabled={selectedIds.size === 0} className={`rounded px-2 py-1 text-xs ${selectedIds.size ? 'bg-yellow-100 hover:bg-yellow-200' : 'bg-yellow-100 opacity-50'}`}>Unarchive</button>

            <div className="hidden md:block h-4 w-px bg-gray-300" />

            {/* Scoring controls */}
            <button type="button" onClick={recomputeSelected} disabled={selectedIds.size === 0} className={`rounded px-2 py-1 text-xs ${selectedIds.size ? 'bg-purple-100 hover:bg-purple-200' : 'bg-purple-100 opacity-50'}`}>
              Recompute Selected
            </button>
            <button type="button" onClick={recomputeFiltered} className="rounded bg-purple-500 px-2 py-1 text-xs text-white hover:bg-purple-600">
              Recompute All (Filtered)
            </button>
          </div>
        </div>

        {/* Pager (above table) */}
        <Pager />

        {/* New Lead Form -- collapsed by default; this is a testing/rare-use
            tool, not part of the normal automated flow. */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setAddLeadOpen((v) => !v)}
            className="rounded bg-gray-100 px-3 py-2 text-sm hover:bg-gray-200"
          >
            {addLeadOpen ? '− Close' : '+ Add Lead (manual / testing)'}
          </button>
          {addLeadOpen && (
            <form onSubmit={handleSubmit} className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-6">
              <input className="rounded border p-2" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
              <input className="rounded border p-2" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required autoComplete="off" />
              <select className="rounded border p-2" value={leadSource} onChange={(e) => setLeadSource(e.target.value)} title="Lead Source">
                <option value="Manual">Manual</option><option value="Discord">Discord</option><option value="X">X (Twitter)</option><option value="Reddit">Reddit</option><option value="Website">Website</option><option value="Referral">Referral</option><option value="Other">Other</option>
              </select>
              <input className="rounded border p-2" placeholder="Discovered by (who/where)" value={discoveredBy} onChange={(e) => setDiscoveredBy(e.target.value)} autoComplete="off" />
              <input className="rounded border p-2" placeholder="Job title (optional)" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} autoComplete="off" />
              <input className="rounded border p-2" placeholder="Company (optional)" value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="off" />
              <input className="rounded border p-2" placeholder="LinkedIn URL (optional)" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} autoComplete="off" />
              <div className="md:col-span-1">
                <textarea className="min-h-[44px] w-full resize-y rounded border p-2" placeholder="Optional Comment" value={commentEdits['new'] ?? ''} onChange={(e) => setCommentEdits((p) => ({ ...p, new: e.target.value }))} />
              </div>
              <button type="submit" className="rounded bg-blue-500 p-2 text-white hover:bg-blue-600">Add Lead</button>
            </form>
          )}
        </div>

        {/* Errors only (no success banner) */}
        {!!message && (
          <p className="mb-4 text-center text-red-500">{message}</p>
        )}

        {/* Leads Table */}
        <div className="overflow-x-auto">
          <table className="w-full table-auto text-left">
            <thead>
              <tr className="bg-gray-2 00">
                <th className="p-2"><input type="checkbox" checked={isAllPageSelected} onChange={toggleSelectAllPage} title="Select all (current page)" /></th>
                <th className="cursor-pointer p-2" onClick={() => handleSort('name')}>Name {sortArrow('name')}</th>
                <th className="cursor-pointer p-2" onClick={() => handleSort('email')}>Email {sortArrow('email')}</th>
                <th className="cursor-pointer p-2" onClick={() => handleSort('status')}>Status {sortArrow('status')}</th>
                <th className="cursor-pointer p-2" onClick={() => handleSort('lead_source')}>Lead Source {sortArrow('lead_source')}</th>
                <th className="cursor-pointer p-2" onClick={() => handleSort('discovered_by')}>Discovered By {sortArrow('discovered_by')}</th>
                {/* Score & Velocity sortable */}
                <th className="cursor-pointer p-2" onClick={() => handleSort('score')}>Score {sortArrow('score')}</th>
                <th className="cursor-pointer p-2" onClick={() => handleSort('velocity')}>Velocity {sortArrow('velocity')}</th>
                <th className="cursor-pointer p-2" onClick={() => handleSort('last_contacted')}>Last Contacted {sortArrow('last_contacted')}</th>
                <th className="cursor-pointer p-2" onClick={() => handleSort('date_added')}>Date Added {sortArrow('date_added')}</th>
                <th className="cursor-pointer p-2" onClick={() => handleSort('updated_at')}>Updated {sortArrow('updated_at')}</th>
                <th className="p-2">Notes</th>
                <th className="p-2">Details</th>
                <th className="p-2">Archived</th>
                <th className="p-2">Delete</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={15} className="p-4 text-center text-sm text-gray-500">Loading…</td></tr>}
              {!loading && filteredLeads.length === 0 && <tr><td colSpan={15} className="p-4 text-center text-sm text-gray-500">No leads match.</td></tr>}

              {!loading && pagedLeads.map((lead) => {
                const hasNote = !!(lead.comment && lead.comment.trim().length > 0);
                const isOpen = openCommentId === lead.id;
                const isSelected = selectedIds.has(lead.id);
                const rowChips = chips[lead.id] || {};

                return (
                  <React.Fragment key={lead.id}>
                    <tr className={`border-t ${getRowClass(lead.status)}`}>
                      <td className="p-2">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelectOne(lead.id)} title="Select row" />
                      </td>
                      <td className="p-2">{lead.name}</td>
                      <td className="p-2">{lead.email}</td>

                      {/* Status, Lead Source, Discovered By: display-only. These are derived
                          from real interactions/creation-time facts, not something to hand-edit
                          after the fact -- see the drill-down page for the actual history. */}
                      <td className="p-2">
                        <span className="rounded px-2 py-1 text-sm bg-gray-100">{lead.status || 'New'}</span>
                      </td>
                      <td className="p-2">
                        <span className="rounded px-2 py-1 text-sm bg-gray-100">{lead.lead_source || '—'}</span>
                      </td>
                      <td className="p-2">
                        <span className="rounded px-2 py-1 text-sm bg-gray-100">{lead.discovered_by || '—'}</span>
                      </td>

                      {/* Score & Velocity cells */}
                      <td className="p-2">
                        <span className={`rounded px-2 py-1 text-sm ${lead.score >= 70 ? 'bg-green-100' : lead.score >= 40 ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                          {lead.score ?? 0}
                        </span>
                      </td>
                      <td className="p-2">
                        <span className={`rounded px-2 py-1 text-sm ${lead.velocity === 'short' ? 'bg-purple-100' : 'bg-gray-100'}`}>
                          {lead.velocity || 'long'}
                        </span>
                      </td>

                      {/* Last Contacted: auto-set by record_interaction() on outbound touchpoints */}
                      <td className="p-2">
                        <span>{formatDateTime(lead.last_contacted)}</span>
                      </td>
                      <td className="p-2">{lead.date_added ? formatDateTime(lead.date_added) : ''}</td>
                      <td className="p-2">{lead.updated_at ? formatDateTime(lead.updated_at) : ''}</td>

                      {/* Notes icon cell */}
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleCommentEditor(lead.id)}
                            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-sm ${hasNote ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700'} hover:bg-gray-200`}
                            title={hasNote ? 'View/Edit note' : 'Add note'}
                          >
                            <span role="img" aria-label="note">🗒️</span>
                            <span>{hasNote ? 'Note' : 'Add'}</span>
                            {hasNote && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-yellow-500" />}
                          </button>
                          {/* Optional: small echo chip in the main row, but editor row also shows it right by the Save button */}
                          {rowChips.note && <MiniChip kind={rowChips.note.kind} label={rowChips.note.label} />}
                        </div>
                      </td>

                      {/* Details: full interaction history + drafts sent to this lead */}
                      <td className="p-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link to={`/leads/${lead.id}`} className="rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600">
                            View →
                          </Link>
                          {rowChips.actions && <MiniChip kind={rowChips.actions.kind} label={rowChips.actions.label} />}
                        </div>
                      </td>

                      <td className="p-2">
                        <label className="inline-flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={!!lead.archived} onChange={(e) => handleArchiveToggle(lead.id, e.target.checked)} />
                          <span>{lead.archived ? 'Yes' : 'No'}</span>
                        </label>
                      </td>

                      <td className="p-2">
                        <button className="text-red-500 hover:text-red-700" onClick={() => handleDelete(lead.id)}>Delete</button>
                      </td>
                    </tr>

                    {/* Inline note editor row — chip shows NEXT TO "Save Note" so it's visible at the action point */}
                    {isOpen && (
                      <tr className="border-t bg-amber-50">
                        <td colSpan={15} className="p-3">
                          <form onSubmit={(e) => handleSaveComment(e, lead.id)} className="flex flex-col gap-2">
                            <textarea
                              className="min-h-[64px] w-full resize-y rounded border p-2"
                              value={commentEdits[lead.id] ?? lead.comment ?? ''}
                              onChange={(e) => handleCommentChange(lead.id, e.target.value)}
                              onBlur={() => handleBlurComment(lead.id)}
                              placeholder="Internal note…"
                              autoComplete="off"
                            />
                            <div className="flex items-center gap-2">
                              <button type="submit" className="rounded bg-blue-500 px-3 py-1 text-sm text-white hover:bg-blue-600">Save Note</button>
                              <button type="button" onClick={() => setOpenCommentId(null)} className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300">Close</button>
                              {/* Chip rendered right here so user sees confirmation beside the Save button */}
                              {rowChips.note && <MiniChip kind={rowChips.note.kind} label={rowChips.note.label} />}
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pager (below table) */}
        <div className="mt-3">
          <Pager />
        </div>

        <button onClick={handleLogout} className="mt-6 text-sm text-gray-500 underline">Logout</button>
      </div>
    </div>
  );
}
